import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { UserStat, bulkUpdateUserSubscription } from '../../../../services/adminService';
import UserRow from './UserRow';
import UserDetailModal from './UserDetailModal';

interface UsersListProps {
    users: UserStat[];
    onRefresh: () => void;
}

type BulkStatus = 'free' | 'basic' | 'pro' | 'inactive';

const UsersList: React.FC<UsersListProps> = ({ users, onRefresh }) => {
    const [selectedUser, setSelectedUser] = useState<UserStat | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkPlanChoice, setBulkPlanChoice] = useState<BulkStatus>('pro');
    const [isBulkApplying, setIsBulkApplying] = useState(false);

    // Se a lista de usuários mudar (filtro, refresh), descarta seleções que já não existem mais na vista
    useEffect(() => {
        setSelectedIds((prev) => {
            const visibleIds = new Set(users.map((u) => u.id));
            const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [users]);

    const handleUserClick = (user: UserStat) => {
        setSelectedUser(user);
        setIsDetailModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsDetailModalOpen(false);
        setSelectedUser(null);
        onRefresh(); // Atualizar dados após fechar modal
    };

    const allSelected = users.length > 0 && selectedIds.size === users.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(users.map((u) => u.id)));
    };

    const toggleSelectUser = (userId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const applyBulkStatus = async (newStatus: BulkStatus, confirmLabel: string) => {
        if (selectedIds.size === 0 || isBulkApplying) return;

        const confirmed = window.confirm(
            `${confirmLabel} para ${selectedIds.size} usuário(s) selecionado(s)?`
        );
        if (!confirmed) return;

        setIsBulkApplying(true);
        const toastId = toast.loading(`Atualizando ${selectedIds.size} usuário(s)...`);

        try {
            const { successCount, failedIds } = await bulkUpdateUserSubscription(
                Array.from(selectedIds),
                newStatus
            );

            if (failedIds.length === 0) {
                toast.success(`${successCount} usuário(s) atualizado(s) com sucesso!`, { id: toastId });
            } else {
                toast.error(
                    `${successCount} atualizado(s), ${failedIds.length} falharam. Tente novamente para os que falharam.`,
                    { id: toastId }
                );
            }

            setSelectedIds(new Set());
            onRefresh();
        } catch (error) {
            console.error('Error applying bulk status:', error);
            toast.error('Erro ao atualizar usuários em massa.', { id: toastId });
        } finally {
            setIsBulkApplying(false);
        }
    };

    if (users.length === 0) {
        return (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-12 text-center border border-border-light dark:border-border-dark">
                <span className="material-symbols-outlined text-6xl text-text-dark/30 dark:text-text-light/30 mb-4">
                    person_search
                </span>
                <p className="text-text-dark/70 dark:text-text-light/70">
                    Nenhum usuário encontrado
                </p>
            </div>
        );
    }

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white dark:bg-gray-900 rounded-xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden"
            >
                {/* Barra de Ações em Massa */}
                {selectedIds.size > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-wrap items-center gap-3 px-6 py-3 bg-primary/10 dark:bg-primary/15 border-b-2 border-primary/30"
                    >
                        <span className="text-sm font-semibold text-text-dark dark:text-white">
                            {selectedIds.size} selecionado(s)
                        </span>
                        <button
                            onClick={() => applyBulkStatus('free', 'Ativar (plano Free)')}
                            disabled={isBulkApplying}
                            className="px-3 py-1.5 rounded-lg bg-green-500/15 hover:bg-green-500 text-green-700 dark:text-green-400 hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                        >
                            Ativar
                        </button>
                        <button
                            onClick={() => applyBulkStatus('inactive', 'Desativar')}
                            disabled={isBulkApplying}
                            className="px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500 text-red-700 dark:text-red-400 hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                        >
                            Desativar
                        </button>
                        <div className="flex items-center gap-2">
                            <select
                                value={bulkPlanChoice}
                                onChange={(e) => setBulkPlanChoice(e.target.value as BulkStatus)}
                                disabled={isBulkApplying}
                                className="px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-sm text-text-dark dark:text-white disabled:opacity-50"
                            >
                                <option value="free">Free</option>
                                <option value="basic">Basic</option>
                                <option value="pro">Pro</option>
                                <option value="inactive">Inativo</option>
                            </select>
                            <button
                                onClick={() => applyBulkStatus(bulkPlanChoice, 'Alterar plano')}
                                disabled={isBulkApplying}
                                className="px-3 py-1.5 rounded-lg bg-primary/15 hover:bg-primary text-primary hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                Aplicar Plano
                            </button>
                        </div>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            disabled={isBulkApplying}
                            className="ml-auto px-3 py-1.5 rounded-lg text-text-dark/70 dark:text-gray-400 hover:text-text-dark dark:hover:text-white text-sm font-medium transition-colors disabled:opacity-50"
                        >
                            Limpar seleção
                        </button>
                    </motion.div>
                )}

                {/* Header da Tabela - Esconde em mobile */}
                <div className="hidden md:flex items-center gap-4 px-6 py-4 bg-primary/20 dark:bg-primary/30 text-sm font-semibold text-text-dark dark:text-white">
                    <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                        }}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-400 text-primary focus:ring-primary flex-shrink-0"
                        aria-label="Selecionar todos"
                    />
                    <div className="grid grid-cols-12 gap-4 flex-1">
                        <div className="col-span-2">Nome</div>
                        <div className="col-span-2">Plano</div>
                        <div className="col-span-1">Leads</div>
                        <div className="col-span-1">Prosp.</div>
                        <div className="col-span-2">Pesquisas</div>
                        <div className="col-span-2">WhatsApp</div>
                        <div className="col-span-1">Tokens</div>
                        <div className="col-span-1 text-right">Ações</div>
                    </div>
                </div>

                {/* Lista de Usuários - Scroll horizontal em mobile */}
                <div className="overflow-x-auto">
                    <div className="min-w-[850px] divide-y divide-gray-200 dark:divide-gray-700">
                        {users.map((user, index) => (
                            <UserRow
                                key={user.id}
                                user={user}
                                index={index}
                                onClick={() => handleUserClick(user)}
                                selected={selectedIds.has(user.id)}
                                onToggleSelect={() => toggleSelectUser(user.id)}
                            />
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Modal de Detalhes */}
            {selectedUser && (
                <UserDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseModal}
                    user={selectedUser}
                />
            )}
        </>
    );
};

export default UsersList;
